import type { Member, CheckIn, DashboardStats, WeekCheckins, ComplianceWeek, WeekOption, AppUser, ContactLogEntry, PortalSettings, ChatMessage } from "./types";

const BASE = (typeof __API_BASE__ !== "undefined" ? __API_BASE__ : "/api/fgnkd") as string;

// ─── Auth token management ───────────────────────────────────────────────
let authToken: string | null = localStorage.getItem("fgnkd_token");
let currentUser: AppUser | null = null;

function authHeaders(): Record<string, string> {
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (authToken) h["Authorization"] = `Bearer ${authToken}`;
  return h;
}

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, { ...options, headers: { ...authHeaders(), ...options?.headers } });
  if (res.status === 401) {
    // Session expired — clear and redirect to login
    logout();
    window.location.href = "/login";
    throw new Error("Session expired");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API error ${res.status}: ${text}`);
  }
  return res.json();
}

export function getAuthToken() { return authToken; }
export function getCurrentUser() { return currentUser; }

export async function login(email: string, password: string): Promise<AppUser> {
  const res = await fetch(`${BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Login failed" }));
    throw new Error(err.error || "Login failed");
  }
  const data = await res.json();
  authToken = data.token;
  currentUser = data.user;
  localStorage.setItem("fgnkd_token", data.token);
  localStorage.setItem("fgnkd_user", JSON.stringify(data.user));
  return data.user;
}

export function logout() {
  if (authToken) {
    fetch(`${BASE}/auth/logout`, {
      method: "POST",
      headers: { "Authorization": `Bearer ${authToken}` },
    }).catch(() => {});
  }
  authToken = null;
  currentUser = null;
  localStorage.removeItem("fgnkd_token");
  localStorage.removeItem("fgnkd_user");
}

export async function checkSession(): Promise<AppUser | null> {
  if (!authToken) return null;
  try {
    const res = await fetch(`${BASE}/auth/me`, { headers: { "Authorization": `Bearer ${authToken}` } });
    if (!res.ok) { logout(); return null; }
    const data = await res.json();
    currentUser = data.user;
    return data.user;
  } catch {
    return null;
  }
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const res = await fetch(`${BASE}/auth/change-password`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to change password");
  }
}

// Restore user from localStorage on load
try {
  const saved = localStorage.getItem("fgnkd_user");
  if (saved && authToken) currentUser = JSON.parse(saved);
} catch {}

export const api = {
  // Members
  getMembers: (params?: { search?: string; status?: string; membership_type?: string; payment_plan?: string }) => {
    const q = new URLSearchParams();
    if (params?.search) q.set("search", params.search);
    if (params?.status) q.set("status", params.status);
    if (params?.membership_type) q.set("membership_type", params.membership_type);
    if (params?.payment_plan) q.set("payment_plan", params.payment_plan);
    const qs = q.toString();
    return fetchJSON<{ members: Member[] }>(`${BASE}/members${qs ? "?" + qs : ""}`);
  },
  getMember: (id: string) =>
    fetchJSON<{ member: Member }>(`${BASE}/members/${id}`),
  createMember: (data: Partial<Member>) =>
    fetchJSON<{ member: Member }>(`${BASE}/members`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(data),
    }),
  updateMember: (id: string, data: Partial<Member>) =>
    fetchJSON<{ member: Member }>(`${BASE}/members/${id}`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify(data),
    }),
  deleteMember: (id: string) =>
    fetchJSON<{ success: boolean }>(`${BASE}/members/${id}`, {
      method: "DELETE", headers: authHeaders(),
    }),
  addWeeks: (id: string, weeks: number) =>
    fetchJSON<{ member: Member }>(`${BASE}/members/${id}/add-weeks`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify({ weeks }),
    }),
  updateFollowUp: (id: string, follow_up_date: string | null) =>
    fetchJSON<{ member: Member }>(`${BASE}/members/${id}/follow-up`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify({ follow_up_date }),
    }),

  // Member check-in history
  getMemberCheckIns: (memberId: string) =>
    fetchJSON<{ checkins: CheckIn[] }>(`${BASE}/members/${memberId}/checkins`),

  // Check-ins
  getCheckIns: (params?: { member_id?: string; week_start?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.member_id) q.set("member_id", params.member_id);
    if (params?.week_start) q.set("week_start", params.week_start);
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.offset) q.set("offset", String(params.offset));
    const qs = q.toString();
    return fetchJSON<{ checkins: CheckIn[]; total: number }>(`${BASE}/checkins${qs ? "?" + qs : ""}`);
  },
  getWeekCheckIns: (weekStart: string) =>
    fetchJSON<WeekCheckins>(`${BASE}/checkins/week/${weekStart}`),
  getCheckIn: (id: string) =>
    fetchJSON<{ checkin: CheckIn }>(`${BASE}/checkins/${id}`),
  createCheckIn: (data: Partial<CheckIn>) =>
    fetchJSON<{ checkin: CheckIn }>(`${BASE}/checkins`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(data),
    }),
  updateCheckIn: (id: string, data: Partial<CheckIn>) =>
    fetchJSON<{ checkin: CheckIn }>(`${BASE}/checkins/${id}`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify(data),
    }),
  deleteCheckIn: (id: string) =>
    fetchJSON<{ success: boolean }>(`${BASE}/checkins/${id}`, {
      method: "DELETE", headers: authHeaders(),
    }),

  // Dashboard
  getDashboard: () => fetchJSON<DashboardStats>(`${BASE}/dashboard`),
  getPaymentIssues: () => fetchJSON<{ members: any[] }>(`${BASE}/payment-issues`),

  // Weeks
  getWeeks: () => fetchJSON<{ weeks: WeekOption[] }>(`${BASE}/weeks`),

  // Analytics
  getCompliance: () => fetchJSON<{ weeks: ComplianceWeek[] }>(`${BASE}/analytics/compliance`),
  getRepeatMisses: () => fetchJSON<{ members: any[] }>(`${BASE}/analytics/repeat-misses`),
  getGrowth: () => fetchJSON<{ growth: any[]; churn: any[] }>(`${BASE}/analytics/growth`),
  getBreakdown: () => fetchJSON<{ by_plan: any[]; by_type: any[] }>(`${BASE}/analytics/breakdown`),
  getExpiringSoon: () => fetchJSON<{ members: any[] }>(`${BASE}/analytics/expiring-soon`),

  // CSV Exports
  exportMembersCsv: () => fetch(`${BASE}/export/members`, { headers: { "Authorization": `Bearer ${authToken}` } }),
  exportCheckinsCsv: (weekStart?: string) => {
    const url = weekStart ? `${BASE}/export/checkins?week_start=${weekStart}` : `${BASE}/export/checkins`;
    return fetch(url, { headers: { "Authorization": `Bearer ${authToken}` } });
  },

  // Contact Log
  getContactLog: (memberId: string) =>
    fetchJSON<{ entries: ContactLogEntry[] }>(`${BASE}/settings/contact-log/${memberId}`),
  addContactLog: (memberId: string, note: string, entry_date?: string) =>
    fetchJSON<{ entry: ContactLogEntry }>(`${BASE}/settings/contact-log`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify({ member_id: memberId, note, entry_date }),
    }),
  updateContactLog: (id: string, note: string, entry_date: string) =>
    fetchJSON<{ entry: ContactLogEntry }>(`${BASE}/settings/contact-log/${id}`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify({ note, entry_date }),
    }),
  deleteContactLog: (id: string) =>
    fetchJSON<{ success: boolean }>(`${BASE}/settings/contact-log/${id}`, {
      method: "DELETE", headers: authHeaders(),
    }),

  // Team Chat
  getChatMessages: (params?: { limit?: number; before?: string }) => {
    const q = new URLSearchParams();
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.before) q.set("before", params.before);
    const qs = q.toString();
    return fetchJSON<{ messages: ChatMessage[] }>(`${BASE}/chat/messages${qs ? "?" + qs : ""}`);
  },
  pollChatMessages: (since: string) =>
    fetchJSON<{ messages: ChatMessage[] }>(`${BASE}/chat/messages/poll?since=${encodeURIComponent(since)}`),
  sendChatMessage: (content: string, mentions?: { member_id: string; member_name: string }[]) =>
    fetchJSON<{ message: ChatMessage }>(`${BASE}/chat/messages`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify({ content, mentions }),
    }),

  // Settings / User Management
  getPortalSettings: () =>
    fetchJSON<{ settings: PortalSettings }>(`${BASE}/settings/portal`),
  updatePortalSetting: (key: string, value: string) =>
    fetchJSON<{ success: boolean }>(`${BASE}/settings/portal`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify({ key, value }),
    }),
  updatePortalSettingsBulk: (settings: Record<string, string>) =>
    fetchJSON<{ success: boolean }>(`${BASE}/settings/portal/bulk`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify({ settings }),
    }),
  getUsers: () =>
    fetchJSON<{ users: AppUser[] }>(`${BASE}/settings/users`),
  createUser: (data: { name: string; email: string; role: string; password?: string }) =>
    fetchJSON<{ user: AppUser }>(`${BASE}/settings/users`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(data),
    }),
  updateUser: (id: string, data: Partial<AppUser & { password?: string }>) =>
    fetchJSON<{ user: AppUser }>(`${BASE}/settings/users/${id}`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify(data),
    }),
  deactivateUser: (id: string) =>
    fetchJSON<{ success: boolean }>(`${BASE}/settings/users/${id}`, {
      method: "DELETE", headers: authHeaders(),
    }),
};